// ==========================================
// src/components/BorinhoChat.jsx
// BORINHO — SEU PARCEIRO DE ESTRADA 🤝
// ✅ Pollinations.AI (grátis, sem chave)
// ✅ Geolocalização do motorista
// ✅ Orienta motorista para Buscar Cargas
// ✅ Atenção total ao motorista
// ✅ Sem música, sem distração
// ==========================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Send, User, Sparkles, Loader2, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';

const POLLINATIONS_URL = 'https://text.pollinations.ai/';
const BOT_NAME = 'Borinho';

const AUTO_CLEAR_TIME = 300000;

// ==========================================
// 🤖 PERSONALIDADE DO BORINHO
// ==========================================

const SYSTEM_PROMPT = `Você é o Borinho, parceiro de estrada dos caminhoneiros no BoraFrete.

SUPER PODERES:
- Você sabe a LOCALIZAÇÃO do motorista
- Você ORIENTA o motorista a usar o menu "Buscar Cargas"
- Você é ATENCIOSO e PRESTATIVO

IMPORTANTE SOBRE CARGAS:
- NUNCA diga que não tem carga
- SEMPRE oriente: "Vai no menu Buscar Cargas, clica em Origem e Destino, filtra sua rota!"

REGRAS:
- Respostas curtas (2-3 parágrafos)
- SEMPRE em português brasileiro
- NUNCA diga que é IA, robô, ou Google
- Assine: "Valeu, tamo junto! 🤜🤛"`;

// ==========================================
// 🧠 BASE DE CONHECIMENTO LOCAL
// ==========================================

const RESPOSTAS_BORINHO = {
  'tabela frete': 'E aí, meu amigo! A tabela de frete mínimo é definida pela ANTT. Dá uma olhada no site antt.gov.br ou no app do BoraFrete! Tamo junto! 🚛',
  'frete minimo': 'A ANTT define o frete mínimo por tipo de carga e região. No app do BoraFrete você vê os valores atualizados! Valeu! 💪',
  'diesel': 'Opa! O diesel tá um absurdo, né? 😤 Hoje tá na faixa dos R$ 6,00 a R$ 6,50 por litro. Dica: abastece nas redes grandes! Tamo junto! ⛽',
  'combustivel': 'O combustível é a dor de cabeça do caminhoneiro! Diesel tá caro, mas dá pra economizar com postos de rede. Bora pra cima! 🚛',
  'antt': 'A ANTT é quem manda nas regras da estrada! Resoluções novas saem toda semana. Acompanha no site antt.gov.br! 📋',
  'rodovia': 'As rodovias brasileiras... tem dia que tá liso, tem dia que parece lunar! 🌙 BR-163 e BR-364 sempre com obras. Confere no Waze! 🛣️',
  'br-163': 'A BR-163 é a estrada da soja! Liga o Mato Grosso aos portos do Pará. Sempre tem obras, vai com calma! 🚛',
  'br-364': 'A BR-364 corta o Brasil de leste a oeste! Cuidado com as curvas e a chuva! Segurança em primeiro lugar! 🛣️',
  'seguro': 'Seguro de carga é OBRIGATÓRIO, meu amigo! Apólice mínima de R$ 60 mil por eixo. Não arrisque andar sem! 🛡️',
  'seguro carga': 'Seguro é lei! Apólice mínima de R$ 60 mil por eixo. Se precisar de corretora, chama o suporte: Sandra ou José no 65-981181997. 🛡️',
  'cadastro': 'Fazer cadastro no BoraFrete é rapidinho! CPF, CNH, dados do caminhão e pronto. Em minutos você já tá vendo cargas! Bora lá! 📱',
  'como cadastrar': 'Cadastro no BoraFrete é fácil: CPF, CNH, dados do caminhão e pronto! Acessa o app e começa a rodar! 💪',
  'pagamento': 'O pagamento no BoraFrete é via PIX assim que a carga é entregue. Rápido, seguro e direto na sua conta! 💰',
  'pix': 'PIX no BoraFrete é na hora! Assim que a empresa confirma a entrega, o dinheiro cai na sua conta. Sem enrolação! 💰',
  'cnh': 'CNH na estrada é sagrada! Não esquece de renovar antes do vencimento. Segurança em primeiro lugar! 🪪',
  'descanso': 'Lei do caminhoneiro manda 11 horas de descanso a cada 24h. Não adianta forçar, a multa é pesada e a vida é mais importante! 💤',
  'jornada': 'Jornada do caminhoneiro: máximo 8h diárias de direção, 11h de descanso. Respeita isso, meu amigo! Sua vida vale mais! 💤',
  'nota fiscal': 'NF-e é obrigatória para todo transporte! A empresa geralmente emite, mas você precisa conferir antes de sair. 📄',
  'imposto': 'Impostos no transporte... complicado! IR, ISS, ICMS... Dica: contrata um contador especializado. Vale cada centavo! 💰',
  'greve': 'Greve de caminhoneiro é um direito, mas tem que ser feita dentro da lei. Acompanha os grupos de motoristas! Tamo junto! ✊',
  'manifestacao': 'Manifestação é direito do cidadão! Sempre com respeito e dentro da lei. Acompanha os grupos de motoristas! Bora pra cima! ✊',
  'solidao': 'Eita, meu amigo... a solidão na estrada é real. 😔 Mas lembra: você não tá sozinho! Liga pra família, ouve música boa! 🙏 "O Senhor é o meu pastor, nada me faltará." — Salmos 23:1',
  'cansado': 'Cansado na estrada? 😔 Para, descansa, bebe uma água. Sua vida vale mais que qualquer prazo! 🙏 "Vinde a mim, todos os que estais cansados..." — Mateus 11:28',
  'familia': 'Saudade da família é o peso mais pesado que o caminhão carrega, né? 😔 Mas lembra: cada km é um km mais perto de casa! 💪',
  'fe': '🙏 "Tudo posso naquele que me fortalece." — Filipenses 4:13\n\nA fé move montanhas e caminhões! Quando a estrada tiver difícil, lembra que Deus tá no volante contigo. Tamo junto! 🤜🤛',
  'versiculo': '🙏 "Não temas, pois eu sou contigo; não te assombres, pois eu sou o teu Deus." — Isaías 41:10\n\nFica firme, meu amigo! Deus tá contigo em cada km! 🚛💨',
  'deus': 'Deus é o melhor copiloto que existe! 🙏 Em cada curva, em cada subida, em cada noite na estrada, Ele tá contigo. "Confie no Senhor de todo o coração." — Provérbios 3:5',
  'borafrete': 'BoraFrete é sua parceira de estrada! Conecta motoristas autônomos com empresas que precisam transportar carga. Cadastro rápido, pagamento via PIX! 🚛',
  'como funciona': 'BoraFrete funciona assim: 1) Cadastra como motorista, 2) Acessa cargas disponíveis, 3) Escolhe a que te interessa, 4) Fecha contrato digital, 5) Transporta e recebe via PIX! 💰',
  'niveis': 'Os níveis do BoraFrete premiam quem mais roda! Quanto mais cargas você transporta, mais pontos ganha e sobe de nível. Cada nível novo libera benefícios! 🏆',
  'contrato': 'Contrato digital no BoraFrete é 100% seguro e válido! Você assina na tela do celular, a empresa assina também. Sem papelada! 📱',
  'suporte': 'Precisa de ajuda? Chama o suporte! Sandra ou José no 65-981181997. Tamo junto pra resolver! 🤜🤛',
  'ajuda': 'Precisa de ajuda? Sandra ou José no 65-981181997. Ou pergunta aqui que eu te ajudo no que puder! Tamo junto! 🤜🤛',
  'sandra': 'Sandra é nossa atendente! Liga pra ela no 65-981181997. Atendimento personalizado e com carinho! 💪',
  'jose': 'José Roberto é o fundador do BoraFrete! Se precisar falar com ele, liga no 65-981181997. Tamo junto! 🤜🤛',
  'contato': 'Fala com a gente! Sandra ou José no 65-981181997. Ou manda mensagem que a gente responde rapidinho! 📱',
  'whatsapp': 'Nosso WhatsApp é 65-981181997. Fala com Sandra ou José! Tamo junto! 🤜🤛',
  'telefone': 'Liga pra gente! 65-981181997. Sandra ou José vão te atender! 📞',
  'default': 'E aí, meu amigo! Essa pergunta é boa, mas eu não tenho essa informação na ponta da língua agora. Pera aí que vou verificar! Ou se for urgente, chama o suporte: Sandra ou José no 65-981181997. Tamo junto! 🤜🤛'
};

function buscarRespostaLocal(texto) {
  const textoLower = texto.toLowerCase();
  const palavrasChave = Object.keys(RESPOSTAS_BORINHO);
  
  for (const chave of palavrasChave) {
    if (chave === 'default') continue;
    if (textoLower.includes(chave)) {
      return RESPOSTAS_BORINHO[chave];
    }
  }
  
  const palavras = textoLower.split(/\s+/);
  for (const palavra of palavras) {
    if (palavra.length < 3) continue;
    for (const chave of palavrasChave) {
      if (chave === 'default') continue;
      if (chave.includes(palavra) && palavra.length > 3) {
        return RESPOSTAS_BORINHO[chave];
      }
    }
  }
  
  return RESPOSTAS_BORINHO.default;
}

// ==========================================
// 🤖 AVATAR BORINHO
// ==========================================

const BorinhoAvatar = ({ onClick, isOpen, hasNewMessages }) => {
  return (
    <div className="relative group">
      {hasNewMessages && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center animate-bounce z-50 shadow-lg border-2 border-white">
          <span className="text-white text-[10px] font-bold">!</span>
        </div>
      )}
      
      {!isOpen && (
        <>
          <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-20" />
          <div className="absolute -inset-2 bg-gradient-to-r from-blue-400 via-cyan-500 to-blue-600 rounded-full blur-xl opacity-30 animate-pulse" />
        </>
      )}
      
      <button
        onClick={onClick}
        className={`
          relative w-16 h-16 rounded-full overflow-hidden 
          shadow-2xl transition-all duration-300 transform
          ${isOpen ? 'scale-110' : 'scale-100 hover:scale-110'}
          border-3 border-white hover:border-blue-300
          focus:outline-none focus:ring-4 focus:ring-blue-500/30
          bg-gradient-to-br from-slate-100 to-white
        `}
      >
        <img
          src="/borinhochat.png"
          alt="Borinho"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-600/10 to-transparent" />
        <div className="absolute bottom-0 right-0 bg-blue-700 px-1 py-0 rounded-tl-md rounded-br-full z-20">
          <span className="text-white text-[6px] font-black">BF</span>
        </div>
      </button>
      
      {!isOpen && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap">
          <div className="bg-slate-900 text-white text-[10px] px-2 py-1 rounded-md shadow-lg flex items-center gap-1 animate-in fade-in">
            <Sparkles className="w-3 h-3 text-cyan-400" />
            <span>E aí, meu amigo!</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 💬 MENSAGEM
// ==========================================

const ChatMessage = ({ message, isUser }) => {
  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`
        w-8 h-8 rounded-full flex-shrink-0 overflow-hidden shadow-sm border-2
        ${isUser ? 'bg-slate-200 border-slate-300' : 'bg-white border-blue-400'}
      `}>
        {isUser ? (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-4 h-4 text-slate-600" />
          </div>
        ) : (
          <img
            src="/borinhochat.png"
            alt="Borinho"
            className="w-full h-full object-cover scale-110"
          />
        )}
      </div>
      
      <div className={`
        max-w-[80%] px-3 py-2 rounded-2xl text-xs shadow-sm
        ${isUser 
          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-tr-sm' 
          : 'bg-white text-slate-800 rounded-tl-sm border border-slate-200'
        }
      `}>
        <p className="leading-relaxed whitespace-pre-line">{message.text}</p>
        <span className={`
          text-[9px] mt-1 block opacity-80
          ${isUser ? 'text-blue-100' : 'text-slate-400'}
        `}>
          {message.time}
        </span>
      </div>
    </div>
  );
};

// ==========================================
// 🚀 COMPONENTE PRINCIPAL
// ==========================================

const BorinhoChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [localizacao, setLocalizacao] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const clearTimerRef = useRef(null);

  // Geolocalização
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocalizacao({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            cidade: 'Buscando...'
          });
          buscarCidade(pos.coords.latitude, pos.coords.longitude);
        },
        (err) => console.log('Sem localização:', err)
      );
    }
  }, []);

  const buscarCidade = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      setLocalizacao(prev => ({
        ...prev, 
        cidade: data.address?.city || data.address?.town || data.address?.village || 'Sua cidade'
      }));
    } catch (e) {
      console.log('Erro buscar cidade:', e);
    }
  };

  // Auto-limpeza 5 minutos
  useEffect(() => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    if (messages.length > 0) {
      clearTimerRef.current = setTimeout(() => {
        setMessages([]);
        setInputValue('');
        setIsTyping(false);
        if (isOpen) {
          const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          setTimeout(() => {
            setMessages([{
              id: Date.now(),
              text: `E aí! 👋 Sou o ${BOT_NAME}! 🚛\n\nTamo junto, meu amigo! Pergunta o que quiser.\n\n⚡ Papo anterior apagado por inatividade.`,
              time: hora,
              isUser: false
            }]);
          }, 100);
        }
      }, AUTO_CLEAR_TIME);
    }
    return () => { if (clearTimerRef.current) clearTimeout(clearTimerRef.current); };
  }, [messages.length, lastActivity, isOpen]);

  useEffect(() => {
    const handleActivity = () => { if (messages.length > 0) setLastActivity(Date.now()); };
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [messages.length]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      setMessages([{
        id: 1,
        text: `E aí, meu amigo! 👋\n\nSou o ${BOT_NAME}, seu parceiro de estrada aqui no BoraFrete! 🚛\n\nPode perguntar o que quiser — frete, diesel, notícias, ANTT, rodovias, ou só bater um papo. Tamo junto! 🤜🤛`,
        time: hora,
        isUser: false
      }]);
      setLastActivity(Date.now());
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => { if (!isOpen) setHasNewMessages(true); }, 25000);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // ==========================================
  // 🤖 CHAMAR POLLINATIONS.AI
  // ==========================================

  const callPollinationsAI = async (userText) => {
    try {
      const response = await fetch(POLLINATIONS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...messages.slice(-3).map(m => ({
              role: m.isUser ? 'user' : 'assistant',
              content: m.text
            })),
            { role: 'user', content: userText }
          ],
          model: 'openai',
          seed: 42
        })
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      const text = await response.text();
      return text || null;
    } catch (err) {
      console.error('Erro Pollinations:', err);
      return null;
    }
  };

  // ==========================================
  // 💬 ENVIAR MENSAGEM
  // ==========================================

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim() || isTyping) return;
    
    setLastActivity(Date.now());
    const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    
    setMessages(prev => [...prev, {
      id: Date.now(),
      text: text,
      time: hora,
      isUser: true
    }]);
    
    setInputValue('');
    setIsTyping(true);

    const textoLower = text.toLowerCase();
    let resposta = '';

    // VERIFICA SE PEDIU CARGAS - ORIENTA A IR NO MENU
    if ((textoLower.includes('carga') && (textoLower.includes('perto') || textoLower.includes('proxim') || textoLower.includes('onde') || textoLower.includes('aqui') || textoLower.includes('buscar'))) || 
        (textoLower.includes('tem carga') || textoLower.includes('carga disponivel') || textoLower.includes('achar carga'))) {
      
      resposta = `E aí, meu amigo! 🚛 O BoraFrete sempre tem cargas boas pra você!\n\n👉 **Vai no menu "Buscar Cargas" no início do site**\n👉 Clica em **Origem** e coloca sua cidade\n👉 Clica em **Destino** e coloca pra onde você quer ir\n👉 Filtra sua rota e **acha a carga certa**!\n\nSimples assim! Bora rodar e ganhar dinheiro! 💪💰`;
    }
    // VERIFICA LOCALIZAÇÃO
    else if (textoLower.includes('onde estou') || textoLower.includes('minha cidade') || textoLower.includes('localização')) {
      if (localizacao?.cidade) {
        resposta = `E aí, meu amigo! 📍 Pelo que eu tô vendo aqui, você tá em **${localizacao.cidade}**!\n\nQuer achar cargas saindo daí? Vai no menu **"Buscar Cargas"**, coloca **${localizacao.cidade}** na origem e escolhe seu destino! 🚛💨`;
      } else {
        resposta = `E aí, meu amigo! Tô tentando descobrir onde você tá, mas o GPS tá demorando... 😅\n\nEnquanto isso, vai no menu **"Buscar Cargas"** e coloca sua cidade na origem que você acha a carga certa! 🚛`;
      }
    }
    // TENTA IA
    else {
      resposta = await callPollinationsAI(text);
      if (!resposta) {
        resposta = buscarRespostaLocal(text);
      }
    }

    setMessages(prev => [...prev, {
      id: Date.now() + 1,
      text: resposta,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      isUser: false
    }]);
    
    setIsTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setHasNewMessages(false);
      setLastActivity(Date.now());
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
      {isOpen && (
        <Card className="mb-3 w-[340px] bg-white rounded-2xl shadow-2xl border-0 overflow-hidden animate-in slide-in-from-bottom-3 fade-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 p-3 flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-md bg-white">
              <img
                src="/borinhochat.png"
                alt="Borinho"
                className="w-full h-full object-cover scale-110"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm flex items-center gap-1.5 truncate">
                {BOT_NAME}
                <Badge className="bg-emerald-400 text-white border-0 text-[8px] px-1.5 py-0">
                  <span className="w-1 h-1 bg-white rounded-full animate-pulse mr-1" />
                  Online
                </Badge>
              </h3>
              <p className="text-blue-100 text-[10px] truncate flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {localizacao?.cidade || 'Buscando localização...'}
              </p>
            </div>
            
            <button 
              onClick={toggleChat}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Área de Mensagens */}
          <div className="h-[400px] overflow-y-auto bg-slate-50 p-3 space-y-3 scroll-smooth">
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} isUser={msg.isUser} />
            ))}
            
            {isTyping && (
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-blue-400 bg-white flex-shrink-0">
                  <img src="/borinhochat.png" alt="Borinho" className="w-full h-full object-cover scale-110" />
                </div>
                <div className="bg-white px-3 py-2 rounded-2xl rounded-tl-sm border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                    <span className="text-[10px] text-slate-500">Borinho está digitando...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-2.5 bg-white border-t border-slate-100">
            <div className="flex gap-2 items-center">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Pergunte sobre cargas, estrada, frete..."
                className="flex-1 h-9 text-xs bg-slate-50 border-slate-200 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl"
                disabled={isTyping}
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isTyping}
                size="sm"
                className="h-9 w-9 p-0 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Dicas rápidas */}
            <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
              <button 
                onClick={() => handleSendMessage('Quero buscar cargas')}
                className="text-[8px] px-2 py-1 bg-blue-50 text-blue-600 rounded-full whitespace-nowrap hover:bg-blue-100"
              >
                🚛 Buscar Cargas
              </button>
              <button 
                onClick={() => handleSendMessage('Onde estou?')}
                className="text-[8px] px-2 py-1 bg-green-50 text-green-600 rounded-full whitespace-nowrap hover:bg-green-100"
              >
                📍 Minha cidade
              </button>
              <button 
                onClick={() => handleSendMessage('Como funciona o BoraFrete?')}
                className="text-[8px] px-2 py-1 bg-orange-50 text-orange-600 rounded-full whitespace-nowrap hover:bg-orange-100"
              >
                ❓ Como funciona
              </button>
            </div>
            
            {/* Suporte */}
            <div className="mt-2 pt-2 border-t border-slate-100">
              <p className="text-[9px] text-slate-500 text-center">
                Precisa de atendimento? <span className="text-blue-600 font-semibold">Sandra ou José</span>
              </p>
              <p className="text-[9px] text-blue-600 text-center font-bold">
                65-981181997
              </p>
            </div>
          </div>
        </Card>
      )}

      <BorinhoAvatar 
        onClick={toggleChat} 
        isOpen={isOpen} 
        hasNewMessages={hasNewMessages} 
      />
    </div>
  );
};

export default BorinhoChat;